/**
 * PDP price × quantity multiplier.
 * Mirrors how the cart drawer reacts: listens for the theme's
 * `quantity-selector:update` event (bubbles from <quantity-selector-component>)
 * and updates any <product-price data-multiply-on-quantity> element on the page.
 *
 * The currency symbol/locale is taken DIRECTLY from Shopify's first render —
 * we only swap the number portion via regex replacement, so whatever currency
 * the active market is set to ($/₹/€/£/¥…) is preserved as-is.
 */
(function () {
  'use strict';

  const TAG = '[price×qty]';
  const ROOT_SELECTOR = 'product-price[data-multiply-on-quantity]';
  const QTY_INPUT_SELECTOR = 'input[name="quantity"]';

  console.log(TAG, 'script loaded');

  function getQuantity() {
    const input = document.querySelector(QTY_INPUT_SELECTOR);
    if (!input) return 1;
    const v = parseInt(input.value, 10);
    return v > 0 ? v : 1;
  }

  function getVisiblePriceEl(root) {
    const regular = root.querySelector('.price__regular:not(.price__hidden) .price');
    if (regular) return regular;
    const sale = root.querySelector('.price__sale:not(.price__hidden) .price-item--sale.price');
    if (sale) return sale;
    return root.querySelector('.price');
  }

  // Per-element cache of: original full text + how to format the number
  // (decSep, thouSep, decimals). Never stores the currency symbol — we keep
  // that intact by string-replacing only the digits in the original text.
  const formatCache = new Map();

  /**
   * @param {string} text
   * @returns {{ decSep: string|null, thouSep: string, decimals: number } | null}
   */
  function detectNumberFormat(text) {
    const match = String(text || '').match(/[\d][\d.,\s]*/);
    if (!match) return null;
    const numStr = match[0].trim();

    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');

    let decSep = null;
    let decimals = 0;

    if (lastDot === -1 && lastComma === -1) {
      decSep = null;
    } else if (lastDot > lastComma) {
      decSep = '.';
      decimals = numStr.length - lastDot - 1;
    } else {
      decSep = ',';
      decimals = numStr.length - lastComma - 1;
    }

    if (decimals > 4) decimals = 0;
    if (decimals === 3 && (numStr.match(/[.,]/g) || []).length > 1) decimals = 0;

    const thouSep = decSep === '.' ? ',' : '.';
    return { decSep, thouSep, decimals };
  }

  /**
   * @param {number} cents
   * @param {{ decSep: string|null, thouSep: string, decimals: number } | null} fmt
   * @returns {string}
   */
  function formatNumberOnly(cents, fmt) {
    if (!fmt) return (cents / 100).toFixed(2);
    const amount = (cents / 100).toFixed(fmt.decimals);
    const [intPart, decPart] = amount.split('.');
    const withThou = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thouSep);
    return decPart !== undefined && fmt.decimals > 0
      ? withThou + fmt.decSep + decPart
      : withThou;
  }

  function update() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) {
      console.log(TAG, 'no <product-price data-multiply-on-quantity> found — toggle is OFF or attribute missing');
      return;
    }
    const qty = getQuantity();
    roots.forEach((root) => {
      const cents = parseInt(root.dataset.unitCents, 10);
      if (!cents) {
        console.warn(TAG, 'missing data-unit-cents on', root);
        return;
      }
      const el = getVisiblePriceEl(root);
      if (!el) {
        console.warn(TAG, 'no visible .price element under', root);
        return;
      }

      const cacheKey = root.dataset.blockId || 'default';
      if (!formatCache.has(cacheKey)) {
        const originalText = el.textContent;
        const fmt = detectNumberFormat(originalText);
        formatCache.set(cacheKey, { originalText, fmt });
        console.log(TAG, 'cached', { originalText, fmt });
      }

      const { originalText, fmt } = formatCache.get(cacheKey);
      const newNumber = formatNumberOnly(cents * qty, fmt);

      // Swap ONLY the numeric portion in the original Shopify-rendered text.
      // Currency symbol, spacing, and any suffix stay exactly as Shopify wrote them.
      const newText = originalText.replace(/[\d][\d.,\s]*/, newNumber);

      console.log(TAG, 'updating', { qty, unitCents: cents, newText });
      el.textContent = newText;
    });
  }

  document.addEventListener('quantity-selector:update', (e) => {
    console.log(TAG, 'event: quantity-selector:update', e.detail);
    update();
  });

  document.addEventListener('change', (e) => {
    if (e.target && e.target.matches && e.target.matches(QTY_INPUT_SELECTOR)) {
      console.log(TAG, 'event: change on quantity input', e.target.value);
      update();
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target && e.target.matches && e.target.matches(QTY_INPUT_SELECTOR)) {
      update();
    }
  });

  function init() {
    console.log(TAG, 'init — readyState:', document.readyState);
    update();
    setTimeout(update, 100);
    setTimeout(update, 500);
    requestAnimationFrame(update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (e) => {
    console.log(TAG, 'pageshow — persisted:', e.persisted);
    update();
    setTimeout(update, 100);
  });

  function observeQuantityInput() {
    const input = document.querySelector(QTY_INPUT_SELECTOR);
    if (!input) {
      setTimeout(observeQuantityInput, 100);
      return;
    }
    const observer = new MutationObserver(() => {
      console.log(TAG, 'mutation observed on quantity input — value:', input.value);
      update();
    });
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    console.log(TAG, 'MutationObserver attached to quantity input');
  }
  observeQuantityInput();
})();
